import {getMerkmal} from "../misc/widget-functions"
import {ROOT_DIALOG_IDENTIFIER_MAP} from "../misc/utils"
import {buildBodyFromPointer} from "./context-area-actions"

export const fallbackColors = {
    primary: "#ffe841",
    secondary: "#3958d5",
    tertiary: "#85b1c1",
    quartary: "#099300",
    quintary: "#cc6f22",
}

export const errorTextDisableBoth =
    "Hausfrauen, Schüler, Studenten sind nicht selbstständig förderfähig. Sie können einen Riestervertrag nur über einen evtl. Ehepartner, der förderfähig ist, abschließen."
export const errorIdDisableRiester = "mDp5jpKKHQnoIh9aCbxcZ"

export const getYearFromVerlauf = (obj, foerderungYear) =>
    obj?.verlauf?.find(({jahr}) => jahr.toString() === foerderungYear)

const findVertragByDurchfuehrungsweg = (vertragsList, weg) =>
    vertragsList?.find((vertrag) => vertrag.durchfuehrungsweg === weg)

export const buildTable = (obj, foerderungYear) => ({
    kunde: getYearFromVerlauf(obj?.kunde, foerderungYear),
    ehepartner: getYearFromVerlauf(obj?.ehepartner, foerderungYear),
    gesamt: getYearFromVerlauf(obj?.gesamt, foerderungYear),
})

export const buildGesamtTable = (obj) => ({
    kunde: obj?.kunde?.gesamt,
    ehepartner: obj?.ehepartner?.gesamt,
    gesamt: obj?.gesamt?.gesamt,
})

export const generateChartVerlauf = (verlauf, theme) => ({
    bottomLabels: verlauf.map(({jahr}) => jahr),
    datasets: [
        {
            label: "Nettobeitrag",
            data: verlauf.map(({nettobeitrag}) => nettobeitrag),
            backgroundColor:
                theme?.AvRiester?.quartary ?? fallbackColors.quartary,
        },
        {
            label: "Kinderzulage",
            data: verlauf.map(({kinderzulage}) => kinderzulage),
            backgroundColor:
                theme?.AvRiester?.secondary ?? fallbackColors.secondary,
        },
        {
            label: "Steuerersparnis",
            data: verlauf.map(({steuerersparnis}) => steuerersparnis),
            backgroundColor:
                theme?.AvRiester?.primary ?? fallbackColors.primary,
        },
        {
            label: "Berufseinsteigerbonus",
            data: verlauf.map(({startbonus}) => startbonus),
            backgroundColor:
                theme?.AvRiester?.quintary ?? fallbackColors.quintary,
        },
        {
            label: "Grundzulage",
            data: verlauf.map(({grundzulage}) => grundzulage),
            backgroundColor:
                theme?.AvRiester?.tertiary ?? fallbackColors.tertiary,
        },
    ],
})

export const buildDonutValue = (data, centerLabel, theme) => ({
    segments: [
        {
            label: "Grundzulage",
            value: data?.grundzulage,
            color: theme?.AvRiester?.tertiary ?? fallbackColors.tertiary,
        },
        {
            label: "Berufseinsteigerbonus",
            value: data?.startbonus,
            color: theme?.AvRiester?.quintary ?? fallbackColors.quintary,
        },
        {
            label: "Kinderzulage",
            value: data?.kinderzulage,
            color: theme?.AvRiester?.secondary ?? fallbackColors.secondary,
        },
        {
            label: "Steuerersparnis",
            value: data?.steuerersparnis,
            color: theme?.AvRiester?.primary ?? fallbackColors.primary,
        },
        {
            label: "Nettobeitrag",
            value: data?.nettobeitrag,
            color: theme?.AvRiester?.quartary ?? fallbackColors.quartary,
        },
    ],
    centerLabel: centerLabel,
})

export const buildDonutValueGesamtfoerderung = (data, centerLabel, theme) => ({
    segments: [
        {
            label: "Grundzulage",
            value: data?.grundzulage,
            color: theme?.AvRiester?.tertiary ?? fallbackColors.tertiary,
        },
        {
            label: "Berufseinsteigerbonus",
            value: data?.startbonus,
            color: theme?.AvRiester?.quintary ?? fallbackColors.quintary,
        },
        {
            label: "Steuerersparnis",
            value: data?.steuerersparnis,
            color: theme?.AvRiester?.primary ?? fallbackColors.primary,
        },
        {
            label: "Nettobeitrag",
            value: data?.nettobeitrag,
            color: theme?.AvRiester?.quartary ?? fallbackColors.quartary,
        },
        {
            label: "Kinderzulage",
            value: data?.kinderzulage,
            color: theme?.AvRiester?.secondary ?? fallbackColors.secondary,
        },
    ],
    centerLabel: centerLabel,
})

const wertentwicklungData = (
    ergebnisAv,
    foerdervergleichAv,
    foerdervergleichSparplan,
    berechnungsvorgabe,
    target,
    theme,
) => ({
    chartSparplan: {
        labels: [""],
        datasets: [
            {
                label: "Vorhandenes Riesterkapital",
                data: [
                    berechnungsvorgabe?.[target]
                        ?.vorgabeUebertragenesRiesterkapital,
                ],
                backgroundColor: theme?.AvRiester?.primary,
            },
            {
                label: "Eigenbeiträge",
                data: [ergebnisAv?.[target]?.gesamt?.eigenbeitrag],
                backgroundColor: theme?.AvRiester?.secondary,
            },
            {
                label: "Förderung",
                data: [
                    ergebnisAv?.[target]?.verlauf?.at(-1)
                        ?.kumulierteSummeFoerderung,
                ],
                backgroundColor: theme?.AvRiester?.tertiary,
            },
            {
                label: "Wertentwicklung",
                data: [ergebnisAv?.[target]?.gesamt?.wertentwicklung],
                backgroundColor: theme?.AvRiester?.quartary,
            },
        ],
    },
    tableData: {
        depot: {
            eigenbeitraege: ergebnisAv?.[target]?.gesamt?.eigenbeitrag,
            foerderung:
                ergebnisAv?.[target]?.verlauf?.at(-1)
                    ?.kumulierteSummeFoerderung,
            vorhandenesRiesterkapital:
                berechnungsvorgabe?.[target]
                    ?.vorgabeUebertragenesRiesterkapital,
            gesamtkapital: ergebnisAv?.[target]?.gesamt?.wertentwicklung,
        },
    },
})

const foerderVergleichRenditen = (
    foerdervergleichAvDepot,
    foerdervergleichRiester,
    target,
    theme,
) => ({
    labels: ["AV-Depot", "Riester"],
    datasets: [
        {
            label: "Rendite",
            data: [
                foerdervergleichAvDepot?.[target]?.einzelRenditen?.[0]?.rendite,
                foerdervergleichRiester?.[target]?.einzelRenditen?.[0]?.rendite,
            ],
            backgroundColor: theme?.AvRiester?.secondary,
        },
    ],
})

const leistungsphaseData = (
    foerdervergleichAvDepot,
    foerdervergleichRiester,
    target,
    theme,
) => ({
    chartAv: {
        bottomLabels: ["lebenslange Rente", "Auszahlplan bis 85"],
        datasets: [
            {
                label: "lebenslange Rente",
                data: [
                    foerdervergleichAvDepot?.[target]?.summen?.nettorente,
                    null,
                ],
                backgroundColor: theme?.AvRiester?.quartary,
            },
            {
                label: "Auszahlplan bis 85",
                data: [
                    null,
                    foerdervergleichRiester?.[target]?.summen?.nettorente,
                ],
                backgroundColor: theme?.AvRiester?.secondary,
            },
        ],
    },
    lebenslangeRente: foerdervergleichAvDepot?.[target]?.summen?.nettorente,
    auszahlplan: foerdervergleichRiester?.[target]?.summen?.nettorente,
})

const buildFoerdervergleichData = (ergebnisKipppunkt, target, theme) => {
    const isBesser = Boolean(
        ergebnisKipppunkt?.[target]?.avDepot?.renditeBesser,
    )
    const infoText = `Aktuell ist für Sie die alte Förderung besser, allerdings lohnt es sich für Sie im Jahr ${ergebnisKipppunkt?.[target]?.avDepot?.renditeBesser?.jahr} in die neue Förderung zu wechseln.`
    const renditeDieseJahrChart = {
        bottomLabels: ["alte Förderung", "neue Förderung"],
        datasets: [
            {
                label: "Rendite",
                data: [
                    ergebnisKipppunkt?.[target]?.riester?.renditeDiesesJahr
                        ?.rendite,
                    null,
                ],
                backgroundColor: theme?.AvRiester?.secondary,
            },
            {
                label: "Rendite",
                data: [
                    null,
                    ergebnisKipppunkt?.[target]?.avDepot?.renditeDiesesJahr
                        ?.rendite,
                ],
                backgroundColor: theme?.AvRiester?.secondary,
            },
        ],
    }
    const renditeJahrChartBesser = {
        bottomLabels: ["alte Förderung", "neue Förderung"],
        datasets: [
            {
                label: "Rendite",
                data: [
                    ergebnisKipppunkt?.[target]?.riester?.renditeBesser
                        ?.rendite,
                    null,
                ],
                backgroundColor: theme?.AvRiester?.secondary,
            },
            {
                label: "Rendite",
                data: [
                    null,
                    ergebnisKipppunkt?.[target]?.avDepot?.renditeBesser
                        ?.rendite,
                ],
                backgroundColor: theme?.AvRiester?.secondary,
            },
        ],
    }
    return {
        renditeJahrChartBesser,
        renditeDieseJahrChart,
        isBesser,
        infoText,
    }
}

const FOERDERUNG_AB_POINTER = "/appstate/riesterRechner/foerderungAb"
const FOERDERUNG_AB_DEFAULT = "2027"

// TODO: Pointer-Migration auf /avdepot/* — atomar mit Duplizierung der Result-Attribute
// in attributes.json + Connection-ID-Patches in d1wYD9IV2MVSCRVd.json. Bis dahin teilt
// sich der contextArea-Pfad die Result-Pointer mit dem workunit-Pfad. Solange beide
// Container nicht gleichzeitig sichtbar sind, ist das risikofrei.
const storeCalculationContextArea = (
    response,
    getDataFromPointer,
    updateData,
    theme,
    messages = [],
) => {
    let foerderungYear = getDataFromPointer(FOERDERUNG_AB_POINTER)
    if (!foerderungYear) {
        updateData(FOERDERUNG_AB_DEFAULT, FOERDERUNG_AB_POINTER)
        foerderungYear = FOERDERUNG_AB_DEFAULT
    }
    const riesterTable = buildTable(response?.ergebnisRiester, foerderungYear)
    const avTable = buildTable(response?.ergebnisAvDepot, foerderungYear)
    const riesterTableGesamt = buildGesamtTable(response?.ergebnisRiester)
    const avTableGesamt = buildGesamtTable(response?.ergebnisAvDepot)
    const avVerlauf = response?.ergebnisAvDepot?.gesamt?.verlauf ?? []
    const riesterVerlauf = response?.ergebnisRiester?.gesamt?.verlauf ?? []
    const {
        ergebnisFoerdervergleichSparplan,
        ergebnisFoerdervergleichAvDepot,
        ergebnisAvDepot,
        ergebnisAvDepotKipppunkt,
        berechnungsvorgabenFoerdervergleichAvDepot,
    } = response

    const leistungsphaseKunde = leistungsphaseData(
        ergebnisFoerdervergleichAvDepot,
        ergebnisFoerdervergleichSparplan,
        "kunde",
        theme,
    )
    const leistungsphaseEhepartner = leistungsphaseData(
        ergebnisFoerdervergleichAvDepot,
        ergebnisFoerdervergleichSparplan,
        "ehepartner",
        theme,
    )
    updateData(leistungsphaseKunde, "/riesterUndAv/leistungsphase/kunde")
    updateData(
        leistungsphaseEhepartner,
        "/riesterUndAv/leistungsphase/ehepartner",
    )

    updateData(
        buildFoerdervergleichData(ergebnisAvDepotKipppunkt, "kunde", theme),
        "/riesterUndAv/foerdervergleich/kunde",
    )
    updateData(
        buildFoerdervergleichData(
            ergebnisAvDepotKipppunkt,
            "ehepartner",
            theme,
        ),
        "/riesterUndAv/foerdervergleich/ehepartner",
    )

    const wertentwicklungKunde = wertentwicklungData(
        ergebnisAvDepot,
        ergebnisFoerdervergleichAvDepot,
        ergebnisFoerdervergleichSparplan,
        berechnungsvorgabenFoerdervergleichAvDepot,
        "kunde",
        theme,
    )
    const wertentwicklungPartner = wertentwicklungData(
        ergebnisAvDepot,
        ergebnisFoerdervergleichAvDepot,
        ergebnisFoerdervergleichSparplan,
        berechnungsvorgabenFoerdervergleichAvDepot,
        "ehepartner",
        theme,
    )
    updateData(wertentwicklungKunde, "/riesterUndAv/wertentwicklung/kunde")
    updateData(
        wertentwicklungPartner,
        "/riesterUndAv/wertentwicklung/ehepartner",
    )

    //Wenn in allen Rechnern die neuen Tabellen eingebaut wurden, werden nur noch diese benötigt
    updateData(avTable, "/riesterUndAv/avTableData")
    updateData(riesterTable, "/riesterUndAv/riesterTableData")
    updateData(avTableGesamt, "/riesterUndAv/avTableGesamt")
    updateData(riesterTableGesamt, "/riesterUndAv/riesterTableGesamt")

    updateData(
        foerderVergleichRenditen(
            ergebnisFoerdervergleichAvDepot,
            ergebnisFoerdervergleichSparplan,
            "kunde",
            theme,
        ),
        "/riesterUndAv/foerdervergleich/renditeChartKunde",
    )
    updateData(
        foerderVergleichRenditen(
            ergebnisFoerdervergleichAvDepot,
            ergebnisFoerdervergleichSparplan,
            "ehepartner",
            theme,
        ),
        "/riesterUndAv/foerdervergleich/renditeChartEhepartner",
    )

    updateData(
        buildDonutValue(riesterTable.gesamt, "bisherige Förderung", theme),
        "/riester/riesterChartData",
    )
    updateData(
        buildDonutValue(avTable.gesamt, "neue Förderung ab 2027", theme),
        "/riester/avChartData",
    )
    updateData(
        buildDonutValueGesamtfoerderung(
            riesterTableGesamt.gesamt,
            "bisherige Förderung",
            theme,
        ),
        "/riester/riesterChartFoerderungData",
    )
    updateData(
        buildDonutValueGesamtfoerderung(
            avTableGesamt.gesamt,
            "neue Förderung ab 2027",
            theme,
        ),
        "/riester/avChartFoerderungData",
    )
    updateData(avTable, "/riester/avTableData")
    updateData(riesterTable, "/riester/riesterTableData")
    updateData(ergebnisAvDepotKipppunkt, "/riester/ergebnisAvDepotKipppunkt")
    updateData(response?.ergebnisAvDepot?.gesamt, "/riester/avTableGesamtData")
    updateData(
        response?.ergebnisRiester?.gesamt,
        "/riester/riesterTableGesamtData",
    )
    updateData(response?.ergebnisAvDepot, "/riester/avTableGesamtDataAll")
    updateData(response?.ergebnisRiester, "/riester/riesterTableGesamtDataAll")
    updateData(generateChartVerlauf(riesterVerlauf, theme), "/riester/verlauf")
    updateData(
        generateChartVerlauf(avVerlauf, theme),
        "/altersvorsorge/verlauf",
    )
    const errorRiester = messages?.some(
        (el) => el?.id === errorIdDisableRiester,
    )
    const errorBoth = messages?.some((el) => el?.text === errorTextDisableBoth)
    updateData(
        String(errorRiester || errorBoth),
        "/riester/showEmptyStateRiester",
    )
    updateData(String(errorBoth), "/riester/showEmptyStateAv")
}

const handleKinderZulageChange = (
    getDataFromPointer,
    updateData,
    sendDataToBackend,
    triggerAction,
    zuordnung,
) => {
    const kinder = getDataFromPointer("/kinder")
    const zulageKinderAnzahl = Number.parseInt(
        getDataFromPointer(
            `/avDepot/${zuordnung === "KUNDE" ? "kunde" : "ehepartner"}/anzahlBeiZulageBeruecksichtigendenKinder`,
        ) ?? "0",
    )

    const currentKinder =
        kinder?.filter((kind) => kind.riesterzuordnung === zuordnung) ?? []
    const filledChilds = Array.from(
        {length: zulageKinderAnzahl},
        (_, i) =>
            currentKinder[i] ?? {
                riesterzuordnung: zuordnung,
                geburtsdatum: "2017-01-02",
                avRiesterZusatzlabel: `${zuordnung === "KUNDE" ? "Kunde" : "Partner"} Geburtsjahr Kind ${i + 1}`,
                kindergeldBisAlter: "18",
            },
    )

    const newKinder = [
        ...(kinder?.filter((kind) => kind.riesterzuordnung !== zuordnung) ??
            []),
        ...filledChilds,
    ]

    const body = buildBodyFromPointer("/kinder", newKinder, getDataFromPointer)

    updateData(newKinder, "/kinder")
    sendDataToBackend(getDataFromPointer("/links/updateAvDepot"), body).then(
        (res) => triggerAction("CALC_CONTEXTAREA_AVDEPOT", res),
    )
}

export const RiesterActions = ({
    getDataFromPointer,
    openRootDialog,
    closeRootDialog,
    updateData,
    getDataFromBackend,
    theme,
    sendDataToBackend,
    triggerAction,
}) => ({
    /**
     * Reine Anzeige-Transformation: die Rohergebnisse kommen als `calcData` vom Enricher,
     * der die Berechnung selbst anstößt. Der frühere Zweig, der sich die Ergebnisse notfalls
     * selbst über /links/getAvDepot holte, ist mit dem Legacy-Pfad entfallen.
     */
    CALC_CONTEXTAREA_AVDEPOT: async (stuff) => {
        if (!stuff?.calcData) return
        storeCalculationContextArea(
            stuff.calcData,
            getDataFromPointer,
            updateData,
            theme,
            stuff.calcData?._messages,
        )
    },
    RIESTER_OPEN_VORGABEN: () => {
        openRootDialog(ROOT_DIALOG_IDENTIFIER_MAP.RIESTER_VS_AV_DEPOT)
    },
    CHANGE_KINDER_ZULAGE_KUNDE: () => {
        handleKinderZulageChange(
            getDataFromPointer,
            updateData,
            sendDataToBackend,
            triggerAction,
            "KUNDE",
        )
    },
    CHANGE_KINDER_ZULAGE_EHEPARTNER: () => {
        handleKinderZulageChange(
            getDataFromPointer,
            updateData,
            sendDataToBackend,
            triggerAction,
            "PARTNER",
        )
    },
    RIESTER_CLOSE_VORGABEN: () => {
        closeRootDialog()
    },
    INIT_RIESTER_THEME: () => {
        updateData(theme?.AvRiester, "/appstate/AvDotsColors")
        setTimeout(() => {
            triggerAction("CALC_CONTEXTAREA_AVDEPOT")
        }, 1000)
    },
    HAS_RIESTER: () => {
        updateData(true, "/appstate/RiesterAvSwitcher")
    },
    RESET_KINDER_VALIDATION: ({widget}) => {
        const pointer = getMerkmal(widget)?.pointer
        updateData(false, `/messages/error/${pointer}`)
    },
})
